import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SearchableSelect, type SearchableSelectOption } from '../../components/ui/SearchableSelect';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorOptionLabel } from '../../lib/doctor-name';
import { useToast } from '../../lib/toast';
import {
  emptyNurseryForm,
  nurseryChildToForm,
  nurseryFormToPayload,
  validateNurseryForm,
  type NurseryFormState,
} from './nursery.form';
import {
  useCreateNurseryChild,
  useNurseryChild,
  useNurseryDoctors,
  useUpdateNurseryChild,
} from './nursery.queries';
import { nurseryBabyStatuses, nurseryBloodGroups, nurseryDeliveryModes, nurseryGenders } from './types';

export type NurseryFormMode = 'create' | 'edit' | 'view';

interface NurseryChildFormPageProps {
  mode: NurseryFormMode;
}

const stateLabels = [
  'Andaman and Nicobar (AN)', 'Andhra Pradesh (AP)', 'Arunachal Pradesh (AR)', 'Assam (AS)', 'Bihar (BR)',
  'Chandigarh (CH)', 'Chhattisgarh (CG)', 'Dadra and Nagar Haveli (DN)', 'Daman and Diu (DD)', 'Delhi (DL)',
  'Goa (GA)', 'Gujarat (GJ)', 'Haryana (HR)', 'Himachal Pradesh (HP)', 'Jammu and Kashmir (JK)',
  'Jharkhand (JH)', 'Karnataka (KA)', 'Kerala (KL)', 'Lakshdweep (LD)', 'Madhya Pradesh (MP)',
  'Maharashtra (MH)', 'Manipur (MN)', 'Meghalaya (ML)', 'Mizoram (MZ)', 'Nagaland (NL)', 'Odisha (OD)',
  'Puducherry (PY)', 'Punjab (PB)', 'Rajasthan (RJ)', 'Sikkim (SK)', 'Tamil Nadu (TN)', 'Tripura (TR)',
  'Uttar Pradesh (UP)', 'Uttarakhand (UK)', 'West Bengal (WB)',
] as const;

const westBengalDistricts = [
  'Bankura', 'Bardhaman', 'Birbhum', 'Cooch Behar', 'Dakshin Dinajpur', 'Darjeeling', 'Hooghly', 'Howrah',
  'Jalpaiguri', 'Kolkata', 'Maldah', 'Murshidabad', 'Nadia', 'North 24 Parganas', 'Paschim Medinipur',
  'Purba Medinipur', 'Purulia', 'South 24 Parganas', 'Uttar Dinajpur',
] as const;

const stateOptions: SearchableSelectOption[] = stateLabels.map((label) => ({ value: label, label }));
const districtOptions: SearchableSelectOption[] = westBengalDistricts.map((label) => ({ value: label, label }));
const genderOptions: SearchableSelectOption[] = nurseryGenders.map((value) => ({
  value,
  label: value === 'Male' ? 'Baby of Male' : value === 'Female' ? 'Baby of Female' : 'Others',
}));
const bloodOptions: SearchableSelectOption[] = nurseryBloodGroups.map((value) => ({ value, label: value }));
const statusOptions: SearchableSelectOption[] = nurseryBabyStatuses.map((value) => ({ value, label: value.toUpperCase() }));
const deliveryOptions: SearchableSelectOption[] = nurseryDeliveryModes.map((value) => ({ value, label: value }));

const Field = ({ label, required = false, error, className = '', children }: {
  label: string;
  required?: boolean;
  error?: string | undefined;
  className?: string;
  children: ReactNode;
}) => (
  <label className={`field nursery-field${error ? ' has-error' : ''} ${className}`.trim()}>
    <span>{label}{required ? <b aria-hidden="true"> *</b> : null}</span>
    {children}
    {error ? <small>{error}</small> : null}
  </label>
);

const mutationError = (error: unknown) => error instanceof ApiError ? error.message : 'Baby registration could not be saved.';

export const NurseryChildFormPage = ({ mode }: NurseryChildFormPageProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { childId } = useParams();
  const readOnly = mode === 'view';
  const detailQuery = useNurseryChild(mode === 'create' ? undefined : childId);
  const doctorsQuery = useNurseryDoctors();
  const createMutation = useCreateNurseryChild();
  const updateMutation = useUpdateNurseryChild(childId ?? '');
  const [form, setForm] = useState<NurseryFormState>(emptyNurseryForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (detailQuery.data) setForm(nurseryChildToForm(detailQuery.data));
  }, [detailQuery.data]);

  const doctorOptions = useMemo(() => {
    const options: SearchableSelectOption[] = (doctorsQuery.data ?? []).map((doctor) => ({
      value: doctor.id,
      label: doctorOptionLabel(doctor.label, doctor.code),
    }));
    const selected = detailQuery.data?.doctor;
    if (selected && !options.some((option) => option.value === selected.id)) {
      options.unshift({ value: selected.id, label: selected.name });
    }
    return options;
  }, [detailQuery.data?.doctor, doctorsQuery.data]);

  const setValue = <K extends keyof NurseryFormState>(key: K, value: NurseryFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    const validationErrors = validateNurseryForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    const payload = nurseryFormToPayload(form);
    const onError = (error: unknown) => showToast(mutationError(error), 'error');

    if (mode === 'create') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          showToast('Baby registered successfully.', 'success');
          void navigate('/nursery');
        },
        onError,
      });
    } else {
      updateMutation.mutate(payload, {
        onSuccess: () => {
          showToast('Baby registration updated successfully.', 'success');
          void navigate('/nursery');
        },
        onError,
      });
    }
  };

  if (mode !== 'create' && detailQuery.isLoading) return <LoadingState />;
  if (mode !== 'create' && detailQuery.isError) {
    return <ErrorState title="Unable to load baby details" message={detailQuery.error instanceof ApiError ? detailQuery.error.message : 'Baby registration could not be loaded.'} action={{ label: 'Back to Nursery', onClick: () => void navigate('/nursery') }} />;
  }

  const mutation = mode === 'create' ? createMutation : updateMutation;

  return (
    <div className="page-stack nursery-page">
      <div className="page-heading nursery-heading">
        <div>
          <p className="eyebrow">Nursery</p>
          <h1>{readOnly ? 'Baby Details' : 'Baby Registration'}</h1>
          <p>{readOnly ? 'Review the complete registered baby record.' : 'Complete the same registration details used by the Nursery workflow.'}</p>
        </div>
        <div className="nursery-heading-actions">
          {readOnly && childId ? <Link className="button button-primary" to={`/nursery/${childId}/edit`}>Edit</Link> : null}
          <Link className="button button-secondary" to="/nursery">Back to list</Link>
        </div>
      </div>

      <form className="nursery-form" onSubmit={(event) => void submit(event)}>
        <section className="nursery-form-section">
          <h2>Personal Information</h2>
          <div className="nursery-personal-grid">
            <Field label="Name of the Patient" required error={errors.name}>
              <input value={form.name} onChange={(event) => setValue('name', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Guardian's Name">
              <input value={form.guardianName} onChange={(event) => setValue('guardianName', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Gender" required error={errors.gender}>
              <SearchableSelect options={genderOptions} value={form.gender} onChange={(value) => setValue('gender', value as NurseryFormState['gender'])} placeholder="Select Gender" disabled={readOnly} />
            </Field>
            <Field label="Blood Group">
              <SearchableSelect options={bloodOptions} value={form.bloodGroup ?? ''} onChange={(value) => setValue('bloodGroup', value as NurseryFormState['bloodGroup'])} placeholder="Select Blood Group" clearable disabled={readOnly} />
            </Field>
            <Field label="Date & Time of Birth of Baby">
              <input type="datetime-local" value={form.dateOfBirth} onChange={(event) => setValue('dateOfBirth', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Year">
              <input value={form.dateOfBirthYear} onChange={(event) => setValue('dateOfBirthYear', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Month">
              <input value={form.dateOfBirthMonth} onChange={(event) => setValue('dateOfBirthMonth', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Day">
              <input value={form.dateOfBirthDay} onChange={(event) => setValue('dateOfBirthDay', event.target.value)} disabled={readOnly} />
            </Field>
          </div>
        </section>

        <div className="nursery-form-columns">
          <section className="nursery-form-section">
            <h2>Address</h2>
            <div className="nursery-section-grid">
              <Field label="Address" required error={errors.address} className="nursery-full">
                <input value={form.address} onChange={(event) => setValue('address', event.target.value)} disabled={readOnly} />
              </Field>
              <Field label="State" required error={errors.state}>
                <SearchableSelect options={stateOptions} value={form.state} onChange={(value) => { setValue('state', value); setValue('district', ''); }} placeholder="Select State" disabled={readOnly} />
              </Field>
              <Field label="District" required error={errors.district}>
                <SearchableSelect options={districtOptions} value={form.district} onChange={(value) => setValue('district', value)} placeholder="Select District" disabled={readOnly} />
              </Field>
              <Field label="Pin No." error={errors.pinCode}>
                <input inputMode="numeric" value={form.pinCode} onChange={(event) => setValue('pinCode', event.target.value)} disabled={readOnly} />
              </Field>
            </div>
          </section>

          <section className="nursery-form-section">
            <h2>Details</h2>
            <div className="nursery-section-grid">
              <Field label="Under Doctor" required error={errors.doctor} className="nursery-full">
                <SearchableSelect options={doctorOptions} value={form.doctor} onChange={(value) => setValue('doctor', value)} placeholder={doctorsQuery.isLoading ? 'Loading doctors...' : 'Select Doctor'} disabled={readOnly || doctorsQuery.isLoading || doctorsQuery.isError} />
              </Field>
              <Field label="Diagnosis">
                <input value={form.diagnosis} onChange={(event) => setValue('diagnosis', event.target.value)} disabled={readOnly} />
              </Field>
              <Field label="Operation">
                <input value={form.operation} onChange={(event) => setValue('operation', event.target.value)} disabled={readOnly} />
              </Field>
              <Field label="Weight of Baby">
                <input value={form.weight} onChange={(event) => setValue('weight', event.target.value)} disabled={readOnly} placeholder="e.g. 2 KG 830 GM" />
              </Field>
            </div>
          </section>
        </div>

        <section className="nursery-form-section">
          <h2>Others Details</h2>
          <div className="nursery-other-grid">
            <Field label="Status Of Baby" required error={errors.babyStatus}>
              <SearchableSelect options={statusOptions} value={form.babyStatus} onChange={(value) => setValue('babyStatus', value as NurseryFormState['babyStatus'])} placeholder="Select Status" disabled={readOnly} />
            </Field>
            <Field label="Mode of Delivery">
              <SearchableSelect options={deliveryOptions} value={form.deliveryMode ?? ''} onChange={(value) => setValue('deliveryMode', value as NurseryFormState['deliveryMode'])} placeholder="Select Mode" clearable disabled={readOnly} />
            </Field>
            <Field label="Admission Date">
              <input type="datetime-local" value={form.admissionDate} onChange={(event) => setValue('admissionDate', event.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Discharge Date" error={errors.dischargeDate}>
              <input type="datetime-local" value={form.dischargeDate} onChange={(event) => setValue('dischargeDate', event.target.value)} disabled={readOnly} />
            </Field>
          </div>
        </section>

        {!readOnly ? (
          <div className="nursery-form-actions">
            <Link className="button button-secondary" to="/nursery">Cancel</Link>
            <button className="button button-primary" type="submit" disabled={mutation.isPending || doctorsQuery.isError}>
              {mutation.isPending ? 'Saving...' : 'Submit'}
            </button>
          </div>
        ) : null}
      </form>
    </div>
  );
};
